import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Eye, EyeOff } from 'lucide-react';
import Loader from '@/components/Loader';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ErrorBanner } from '@/components/common/ErrorBanner';
import { useLoginMutation } from '@/store/api/authApi';
import { useAppDispatch } from '@/hooks/redux';
import { setToken } from '@/store/slices/authSlice';

const loginSchema = z.object({
  email: z
    .string()
    .trim()
    .min(1, 'Email is required')
    .regex(/^[^\s@]+@[^\s@]+\.[^\s@]+$/, 'Please enter a valid email address'),

  password: z
    .string()
    .min(8, 'Password must be at least 8 characters')
    .max(18, 'Password must not exceed 18 characters'),
});

type LoginFormData = z.infer<typeof loginSchema>;

export function LoginPage() {
  const [showPassword, setShowPassword] = useState(false);
  const [loginMutation, { isLoading, error }] = useLoginMutation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const from = (location.state as any)?.from?.pathname || '/dashboard';

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
  });

  const onSubmit = async (data: LoginFormData) => {
    try {
      const result = await loginMutation({
        ...data,
        panelType: import.meta.env.VITE_USER_PANEL || 'adminPanel',
      }).unwrap();
      dispatch(setToken(result?.accessToken));
      navigate(from, { replace: true });
    } catch (err) {
      // Error handled by RTK Query
    }
  };

  return (
    <>
      {isLoading && <Loader />}

      <div className="space-y-6">
        <div className="space-y-2 text-center">
          <h1 className="text-2xl sm:text-3xl md:text-4xl font-lato font-semibold tracking-tight">
            Sign in to your account
          </h1>
          <p className="text-sm text-muted-foreground">
            Enter your email and password to access your account
          </p>
        </div>

        {error && (
          <ErrorBanner
            type="error"
            title="Login Failed"
            message={(error as any)?.data?.message || 'Invalid email or password'}
          />
        )}

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div className="space-y-2">
            <Label
              className="block font-20 font-lato font-medium text-[#333333] mb-1"
              htmlFor="email"
            >
              Email Address
            </Label>
            <Input
              id="email"
              type="email"
              placeholder="Enter your email"
              {...register('email')}
              className={`${
                errors.email ? 'border-destructive' : ''
              } placeholder:text-[16px] placeholder:font-light placeholder:font-lato`}
            />
            {errors.email && <p className="text-sm text-destructive">{errors.email.message}</p>}
          </div>

          <div className="space-y-2">
            <Label
              className="block font-20 font-lato font-medium text-[#333333] mb-1"
              htmlFor="password"
            >
              Password
            </Label>
            <div className="relative">
              <Input
                id="password"
                type={showPassword ? 'text' : 'password'}
                placeholder="Enter your password"
                maxLength={18}
                minLength={8}
                {...register('password')}
                className={`${
                  errors.password ? 'border-destructive' : ''
                } pr-10 placeholder:text-[16px] placeholder:font-light placeholder:font-lato`}
              />

              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
              </Button>
            </div>
            {errors.password && (
              <p className="text-sm text-destructive">{errors.password.message}</p>
            )}
          </div>

          <Button type="submit" disabled={isLoading} className="w-full">
            {isLoading ? (
              <span className="flex items-center gap-2">
                <span className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                Logging in...
              </span>
            ) : (
              <span
                className="
                  font-24
                  w-full
                  h-10
                  font-semibold
                  font-spectral
                  text-black
                  uppercase
                  bg-white
                  inner-shadow-blue
                  rounded-lg
                  flex
                  justify-center
                  items-center
                  cursor-pointer
                  disabled:opacity-60
                  hover:bg-[linear-gradient(to_right,#082261,#34065c)]
                  hover:text-white
                  transition-all
                  duration-500
                  ease-in-out
                "
              >
                Login
              </span>
            )}
          </Button>
        </form>
      </div>
    </>
  );
}
